'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Flex, Text, Box, rem, ActionIcon } from '@mantine/core';

// UI local Components
import LabelSize from '@ui-kit/atoms/LabelSize';
import ResourceIcon from '@features/Resource/ResourceIcon';
import IconInProgress from '@ui-kit/atoms/LoaderIcon';

// Context
import { WorkspaceLayoutContext } from '@layouts/WorkspaceLayout/context';

// Store
import { cancelUpload } from '../store/uploadingFiles';

// Types
import { ResourceUploadStatus } from '@app-types/blackroom-app';
import { ResourceUploadActivityProps } from './types';

// Assets
import {
  IconCircleCheck as IconDone,
  IconCircleX as IconFailed,
  IconClockPause as IconPending,
  IconX,
} from '@tabler/icons-react';

// Style
import { palette } from '@ui-kit/colors';
import * as classes from './styles/index.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceUploadActivity({
  resourceUploads,
}: ResourceUploadActivityProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspaceLayoutContext = useContext(WorkspaceLayoutContext);
  const workspaceId = workspaceLayoutContext.workspace?.id as string;

  // Style

  /* ***************************** RENDER HELPERS ***************************** */

  function getUploadStatusIcon(status: ResourceUploadStatus) {
    switch (status) {
      case ResourceUploadStatus.Pending:
        return <IconPending />;
      case ResourceUploadStatus.Done:
        return <IconDone fill={palette.blackroomGreen} />;
      case ResourceUploadStatus.Failed:
        return <IconFailed fill={palette.blackroomRed} />;
      case ResourceUploadStatus.InProgress:
        return <IconInProgress loading />;
      default:
        return <IconFailed fill={palette.blackroomRed} />;
    }
  }

  /* ******************************** RENDERING ******************************* */
  return (
    <Box className={classes.resourceUploadActivity}>
      {resourceUploads.map((ru) => {
        const { uploadId, name, size } = ru;
        const resourceId = ru.id as string;
        const resourceType = ru.type as string;
        const isInProgress =
          ru.uploadStatus === ResourceUploadStatus.InProgress;
        return (
          <Flex
            key={uploadId}
            className={classes.resourceUpload}
            align="center"
            justify="space-between"
          >
            <Flex align="center" gap={8} style={{ minWidth: 0, flex: 1 }}>
              <Box className={classes.iconWrapper}>
                <ResourceIcon
                  className={isInProgress ? classes.inprogress : undefined}
                  resource={ru}
                  size={16}
                />
              </Box>

              <Link
                className={classes.link}
                to={`/workspace/${workspaceId}/${resourceType}/${resourceId}`}
                style={{
                  minWidth: 0,
                  ...(isInProgress
                    ? { cursor: 'default', pointerEvents: 'none' as const }
                    : undefined),
                }}
              >
                <Text
                  className={isInProgress ? classes.inprogress : undefined}
                  size={rem(12)}
                  truncate
                >
                  {name}
                </Text>
              </Link>
            </Flex>

            <Flex gap={8} align="center">
              {!isInProgress ? (
                <LabelSize size={rem(11)}>{size}</LabelSize>
              ) : (
                <ActionIcon
                  size={18}
                  variant="subtle"
                  color="gray"
                  onClick={() => cancelUpload(uploadId)}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
              {getUploadStatusIcon(ru.uploadStatus as ResourceUploadStatus)}
            </Flex>
          </Flex>
        );
      })}
    </Box>
  );
}

export default ResourceUploadActivity;
